import React from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import environment from '../../environment';
import isFunction from 'lodash/isFunction';
import { changeLoginStatus, setMFAInformation } from '../../actions/index';
import * as SessionAnalyticsService from 'services/SessionAnalyticsService/SessionAnalyticsService';
import { logout } from '../../api/user';
import { sendTestTicketsToExpert } from '../../api/tickets';
import { changeTab } from '../../actions/creatorsSync';
import { updateUser } from '../../actions';
import { getLoginRoleInfo, syncStoreWithCurrentUserState } from '../../util/loaderUtils';
import ReCAPTCHA from 'react-google-recaptcha';
import { rawLightGray2, rawRed3, rawBlue1 } from 'components/common/rawColors';
import { ReactComponent as ShowPasswordIcon } from '../../assets/images/SignUpIcons/showPasswordIcon.svg';
import { ReactComponent as HidePasswordIcon } from '../../assets/images/SignUpIcons/hidePasswordIcon.svg';
import {
  Container,
  LoginBox,
  Title,
  Error,
  Form,
  ResetLink,
  ResetLinkContainer,
  ReCAPTCHAWrapper,
  SubmitWrapper,
  Submit,
  PrivacyAndTermsWrapper,
  SmallText,
  SmallLinkExternal,
  ContentWrapper,
  PasswordInput,
  PasswordWrapper,
  PasswordIcon,
  BackLinkWrapper,
  TextInput,
} from './LoginPage.styles';
import { handleError } from 'services/ErrorService/ErrorService';
import AuthLayout from '../AuthLayout/Old/AuthLayout';
import AuthService from 'services/authService';
import { ACCOUNT_USER } from '../../constants/roles';
import { getExpertById } from '../../api/expertQueries/expertQueriesGraphQL';
import LoadingComponent from '../LoadingComponent/LoadingComponent';
import { BackLinkButton } from '../common/buttonsComponents';
import noop from 'lodash/noop';

import * as SimplrErrors from '@asurion-core/typescript-utils/SimplrErrors';
import { WebObservability } from '@asurion-core/frontend-utils';
import makeRequest from 'api/makeRequest';
import { maskEmailAddress } from '../../util';

class LoginPage extends React.Component {
  state = {
    username: '',
    password: '',
    googleRecaptcha: '',
    action: '',
    loggedIn: false,
    error: '',
    disabled: true,
    showPassword: false,
    passwordFocus: false,
    loading: true,
    usernamePrefilled: false,
    usernameDisabled: false,
  };

  constructor(props) {
    super(props);
    this.googleReCaptchaRef = React.createRef();
  }

  async componentDidMount() {
    const { preLoginEmail, ssoConfig } = this.props;

    if (!preLoginEmail || !ssoConfig) {
      this.props.history.push('/');

      return;
    }

    if (ssoConfig?.enableSSO) {
      this.props.history.push('/sso-login');

      return;
    }

    this.setState({ username: preLoginEmail.toLowerCase(), disabled: false, loading: false });
  }

  handlePasswordChange = (e) => {
    const hasPassword = e.target.value.length > 0;
    const hasUsername = this.state.username.length > 0;
    const isValidRecaptcha = !environment.google.isGoogleReCaptchaEnabled || this.state.googleRecaptcha.length > 0;
    this.setState({
      password: e.target.value,
      error: '',
      disabled: !(hasUsername && hasPassword && isValidRecaptcha),
    });
  };

  handleChangeReCaptcha = (googleRecaptcha) => {
    const hasPassword = this.state.password.length > 0;
    const hasUsername = this.state.username.length > 0;
    const isValidRecaptcha = !environment.google.isGoogleReCaptchaEnabled || googleRecaptcha.length > 0;
    this.setState({
      googleRecaptcha,
      error: '',
      disabled: !(hasPassword && hasUsername && isValidRecaptcha),
    });
  };

  resetCaptcha = () => {
    if (
      this.googleReCaptchaRef &&
      this.googleReCaptchaRef.current &&
      isFunction(this.googleReCaptchaRef.current.reset)
    ) {
      this.googleReCaptchaRef.current.reset();
    }
  };

  validateInputs = () => {
    let error = '';
    const { username, password, googleRecaptcha } = this.state;
    if (!username) {
      error = 'User name required';
    } else if (!password) {
      error = 'Password required';
    } else if (environment.google.isGoogleReCaptchaEnabled && !googleRecaptcha) {
      error = 'Please confirm you are not a robot';
    }
    this.setState({ error });

    return error.length === 0; // returns true if there are no errors and inputs are valid
  };

  signIn = async (event) => {
    event.preventDefault();
    if (this.validateInputs()) {
      try {
        const loginParams = {
          email: this.state.username.toLowerCase(),
          password: this.state.password,
          googleRecaptcha: this.state.googleRecaptcha,
        };

        this.setState({ disabled: true });

        const URL = `${environment.services.authServiceUrl}/v1/user/login`;
        const { result: user } = await makeRequest(URL, {
          method: 'POST',
          data: loginParams,
        });

        if (user && !user.id) {
          await this.props.setMFAInformation(user.token, user.phoneNumberLastDigits);
          this.props.history.push('/mfa');
        } else {
          if (!user || !user.sessionToken) {
            throw new Error('Login attempt has failed. Please try again.');
          }
          await AuthService.login(user.sessionToken);

          await syncStoreWithCurrentUserState(this.props.updateUser);
          const currentUser = AuthService.current();

          // Account users who only have access to Logic Builder should be redirected there (SPD-12844)
          const isAccountUser = currentUser.roles?.includes(ACCOUNT_USER);
          if (isAccountUser) {
            window.location.href = '/logic-builder/';
            return;
          }

          // get the expert id
          const simplrExpertId = currentUser.simplrExpert?.id;
          const simplrExpert = simplrExpertId ? await getExpertById(simplrExpertId) : null;

          // if expert id exists then they are an expert
          if (simplrExpert) {
            if (simplrExpert.isActive) {
              this.setState({ loggedIn: true });
              this.props.changeLoginStatus(true);

              // specialist portal default tab is All tab
              const { isAdmin, isAdminOrAccountUser } = await getLoginRoleInfo();
              if (isAdminOrAccountUser) {
                this.props.changeTab('all');
              } else if (simplrExpert.expertType === 'NEW') {
                // In most cases this will not be called because expert NEW status will be set to TRAINING on signup
                // but if anything goes wrong this ensures that initial login leads to test tickets sent
                await sendTestTicketsToExpert(simplrExpert.id);
              }

              /** NOTE(apoorvyadav1|SPD-13362|2025-10-15):
               * Previously, FullStory was initialized when the website loaded, which is not ideal for privacy compliance.
               * Initialize FullStory if user is logged in and it's not already initialized
               */
              SessionAnalyticsService.initializeSessionAnalyticsServices();

              SessionAnalyticsService.identify(simplrExpert.id, {
                name: `${simplrExpert.firstName} ${simplrExpert.lastName}`,
                email: simplrExpert.email,
                specialist: isAdmin,
              });

              if (environment.observability.isTelemetryEnabled) {
                WebObservability.setSession(currentUser.sessionToken);
                WebObservability.setUser({
                  id: simplrExpert.id,
                  username: currentUser.username,
                  email: maskEmailAddress(simplrExpert.email),
                });
              }

              SessionAnalyticsService.event('User Logged In');
            } else {
              // account is inactive, don't allow them to login.
              logout();
            }
          } else {
            // otherwise its an SMB
            logout();
          }
        }
      } catch (err) {
        let errorMessage = 'Login failed';

        if (err.response?.code === SimplrErrors.Codes.WEAK_PASSWORD) {
          errorMessage = 'Your password has expired. To reset it, please click on “Forgot Your Password”';
        } else if (typeof err.message === 'string') {
          errorMessage = err.message;
        } else if (err.message?.message) {
          errorMessage = err.message.message;
        }

        this.setState({ error: errorMessage }, this.resetCaptcha);
        handleError({ error: err });
      }
    }
  };

  togglePassword = () => {
    this.setState({
      showPassword: !this.state.showPassword,
    });
  };

  getViewPasswordIconColor = () => {
    if (this.state.passwordFocus) {
      return rawBlue1;
    } else if (this.state.error) {
      return rawRed3;
    }

    return rawLightGray2;
  };

  render() {
    if (this.state.loading) {
      return <LoadingComponent delay={0} marginTop={'0px'} />;
    }

    return (
      <AuthLayout>
        <Container>
          <LoginBox>
            <Form name="signIn" action="expert" onSubmit={(e) => this.signIn(e)}>
              <ContentWrapper>
                <Title>Sign In</Title>
                <div>
                  <TextInput
                    onChange={() => noop()} // this is here so that password managers cannot modify this field
                    field="username"
                    value={this.state.username}
                    placeholder="User name"
                    inputType="text"
                    disabled={true}
                  />
                </div>
                <PasswordWrapper>
                  <PasswordInput
                    className={this.state.error ? 'error' : ''}
                    onChange={(e) => this.handlePasswordChange(e)}
                    field="password"
                    placeholder="Password"
                    type={this.state.showPassword ? 'text' : 'password'}
                    onFocus={() => this.setState({ passwordFocus: true })}
                    onBlur={() => this.setState({ passwordFocus: false })}
                  />
                  <PasswordIcon onClick={this.togglePassword}>
                    {!this.state.showPassword ? (
                      <ShowPasswordIcon stroke={this.getViewPasswordIconColor()} width="16px" height="16px" />
                    ) : (
                      <HidePasswordIcon stroke={this.getViewPasswordIconColor()} width="16px" height="16px" />
                    )}
                  </PasswordIcon>
                </PasswordWrapper>
                {!!this.state.error && <Error>{this.state.error}</Error>}
                <ReCAPTCHAWrapper hidden={!environment.google.isGoogleReCaptchaEnabled} data-testid={'recaptcha'}>
                  <ReCAPTCHA
                    sitekey={environment.google.googleReCaptchaClientKey}
                    onChange={this.handleChangeReCaptcha}
                    name="googleRecaptcha"
                    value={this.state.googleRecaptcha}
                    ref={this.googleReCaptchaRef}
                  />
                </ReCAPTCHAWrapper>
                <PrivacyAndTermsWrapper hidden={!environment.google.isGoogleReCaptchaEnabled}>
                  <SmallText>
                    This page is protected by reCAPTCHA and subject to our{' '}
                    <SmallLinkExternal target="_blank" href="https://www.simplr.ai/privacy-policy">
                      Google Privacy Policy{' '}
                    </SmallLinkExternal>
                    and{' '}
                    <SmallLinkExternal target="_blank" href="https://www.simplr.ai/terms-of-service">
                      Terms of Service
                    </SmallLinkExternal>
                  </SmallText>
                </PrivacyAndTermsWrapper>

                <SubmitWrapper>
                  <BackLinkWrapper>
                    <BackLinkButton to="/">Back</BackLinkButton>
                  </BackLinkWrapper>
                  <Submit type="submit" value="Sign in" disabled={this.state.disabled || this.state.error}>
                    Sign in
                  </Submit>
                </SubmitWrapper>
                <ResetLinkContainer>
                  <ResetLink to="/password/reset">Forgot your password?</ResetLink>
                </ResetLinkContainer>
              </ContentWrapper>
            </Form>
          </LoginBox>
        </Container>
      </AuthLayout>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    user: state.user,
    preLoginEmail: state.authReducer.preLoginEmail,
    ssoConfig: state.authReducer.ssoConfig,
  };
};
const mapDispatchToProps = (dispatch) => {
  return {
    changeLoginStatus: (isLoggedIn) => dispatch(changeLoginStatus(isLoggedIn)),
    changeTab: (tabName) => dispatch(changeTab(tabName)),
    updateUser: (user) => dispatch(updateUser(user)),
    setMFAInformation: (mfaToken, phoneNumberLastDigits) =>
      dispatch(setMFAInformation(mfaToken, phoneNumberLastDigits)),
  };
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(LoginPage));
